import { makeStyles, Paper, Theme, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React, { CSSProperties } from 'react';
import { FontIcon } from '@moderepo/mode-app-components';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectAllEntityChildren, useSelectProject } from '../../utils/dataStateHooks';
import { EntitiesListingViewType } from '../../components';

const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            padding       : theme.spacing(1),
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            '&.clickable' : {
                cursor: 'pointer',
            },
        },

        header: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
        },

        headerIcon: {
            marginRight: theme.spacing(1),
        },

        headerTitle: {
            textTransform: 'capitalize',
        },

        statValue: {
        },

        [theme.breakpoints.up('md')]: {
            root: {
                padding: theme.spacing(2),
            },
        },

        [theme.breakpoints.up('lg')]: {
            root: {
                padding: theme.spacing(3),
            },
        },
    };
}, { name: 'EntitiesStatisticContainer', index: 1 });


export interface EntitiesStatisticContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entitiesStatisticSettings: {
        readonly labelKey: string;
        readonly icon: string;
        readonly entityClasses: readonly string[];
        // The viewType to use when we take the user to the entities listing page
        readonly entitiesListingViewType?: EntitiesListingViewType | undefined;
    };
    readonly onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined)=> void | undefined;
}


/**
 * This is a component that displays statistic for 1 set of entity classes. It will fetch and count all the entities for the specified
 * entityClasses.
 */
export const EntitiesStatisticContainer: React.FC<EntitiesStatisticContainerProps> = (props: EntitiesStatisticContainerProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);

    // Fetch the entities for the list of entity classes specified in the entitiesStatisticSettings
    const entities = useSelectAllEntityChildren(props.projectId, undefined, {
        entityClasses: props.entitiesStatisticSettings.entityClasses.join(','),
    });

    const classes = useStyle();

    return (
        <Paper
            className={clsx(props.className, classes.root, props.onClick && 'clickable')}
            elevation={2}
            onClick={props.onClick}
        >
            <Typography
                className={classes.header}
                gutterBottom
                component="h3"
                variant="h5"
            >
                <FontIcon className={classes.headerIcon} iconName={props.entitiesStatisticSettings.icon} />
                <div className={classes.headerTitle}>
                    {trans(props.entitiesStatisticSettings.labelKey)}
                </div>
            </Typography>
            <Typography
                className={classes.statValue}
                component="h3"
                variant="h4"
            >
                {entities?.length ?? 0}
            </Typography>
        </Paper>
    );
};
